import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { STEP } from "#src/constants/step.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";

import { useStepUnderwritingStore } from "#src/stores/step-underwriting.ts";
import { useStepOwnerStore } from "#src/stores/step-owner.ts";
import {
  getPartyStore,
  useEappStore,
} from "#src/stores/electronic-application.ts";
import {
  INSURED_TYPES,
  useInsuredStore,
  usePrimaryInsuredStore,
  useJointInsuredStore,
} from "#src/stores/insured.ts";
import { useStepSecondaryAddresseeStore } from "#src/stores/step-secondary-addressee.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed, type Ref } from "vue";
import { type InsuredType } from "#src/types/Insured.ts";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    payment: required(REQS.PAYMENT),
    payorName: required(REQS.PAYOR_NAME),
    payorAddress: required(REQS.PAYOR_ADDRESS),
    payorEmail: required(REQS.PAYOR_EMAIL),
    payorPhone: required(REQS.PAYOR_PHONE),
    payorSsn: required(REQS.PAYOR_PARTY_ID),
    payorTin: required(REQS.PAYOR_PARTY_ID),
    payorBirthdate: required(REQS.PAYOR_BIRTHDATE),
    payorFormationDate: required(REQS.PAYOR_FORMATION_DATE),
    payorRelationship: required(REQS.PAYOR_RELATIONSHIP),
    eftDraftDayOfMonth: required(REQS.PAYOR_DRAFT_DAY_OF_MONTH),
    payorSignerName: required(REQS.PAYOR_SIGNER_NAME),
    payorSignerEmail: required(REQS.PAYOR_SIGNER_EMAIL),
    payorTaxType: required(REQS.PAYOR_TAX_TYPE),
  };
}

interface PaymentStep extends BaseStepStore, ToSetupState<PaymentState> {
  canSelectPayor: Ref<boolean>;
  reconcilePayorRoles(): Promise<void>;
  reconcileDualInsuredSinglePolicy(): Promise<void>;
  makePrimaryInsuredPayor(): Promise<void>;
  deleteJointNonInsuredPayors(): Promise<void>;
  reconcileInactiveOwnerStepPayors(): Promise<void>;
  makeOwnerPayorForInsured(insuredType: InsuredType): Promise<void>;
  reconcileActiveOwnerStepPayors(): Promise<void>;
  defaultPayorRoleToOwner(): Promise<void>;
  setStepRequirements(srq: DataRequirements): void;
}

interface PaymentState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

function initialState() {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializePaymentStore(pinia: Pinia): PaymentStep {
  const state = generateInitialState<PaymentState>(initialState());

  function $reset() {
    resetState<PaymentState>(state, initialState());
  }

  const step = computed(() => STEP.PAYMENT);
  const canSelectPayor = computed(() =>
    Object.values(state.requiredFields.value).includes(true)
  );
  const previous = computed(() => {
    const secondary = useStepSecondaryAddresseeStore(pinia);
    if (secondary.inactive) return secondary.previous;
    return secondary.step;
  });
  const next = computed(() => {
    const underwriting = useStepUnderwritingStore(pinia);
    return underwriting.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
    state.inactive.value = !Object.values(state.requiredFields.value).includes(
      true
    );
  }
  async function beforeLoadAction() {
    await reconcilePayorRoles();
    return true;
  }
  async function reconcilePayorRoles() {
    const eApp = useEappStore(pinia);
    const ownerStep = useStepOwnerStore(pinia);

    if (eApp.isDualInsured && !eApp.isMultiplePolicies)
      await reconcileDualInsuredSinglePolicy();

    if (canSelectPayor.value) return defaultPayorRoleToOwner();

    if (ownerStep.inactive) return reconcileInactiveOwnerStepPayors();
    return reconcileActiveOwnerStepPayors();
  }
  // Single Policy Dual Insured Payor Role Reconciliation
  async function reconcileDualInsuredSinglePolicy() {
    // Only one policy = only insured's payor = delete joint payor
    const joint = useJointInsuredStore(pinia);

    if (!joint.payorKey) return;
    await joint.deletePayor();
  }

  // Inactive Owner STEP Payor Role Reconciliation
  async function makePrimaryInsuredPayor() {
    const insured = usePrimaryInsuredStore(pinia);

    if (insured.payorKey && insured.payorKey !== INSURED_TYPES.INSURED) {
      await insured.deletePayor();
    }
    if (!insured.payorKey)
      await insured.addPayorRole({ insured_id: insured.id });
  }
  async function deleteJointNonInsuredPayors() {
    const joint = useJointInsuredStore(pinia);

    const jointPayorIsAnInsured = [
      INSURED_TYPES.INSURED,
      INSURED_TYPES.JOINT,
    ].includes(joint.payorKey);

    if (!joint.payorKey || jointPayorIsAnInsured) return;
    await joint.deletePayor();
  }
  async function reconcileInactiveOwnerStepPayors() {
    const eApp = useEappStore(pinia);
    // Inactive Owner STEP = insured is payor = Delete non-insured payors, add insured payor role
    await makePrimaryInsuredPayor();

    // Inactive Owner STEP + multiple policies = insured or joint insured is payor = Delete non-insured payors
    if (eApp.isMultiplePolicies) await deleteJointNonInsuredPayors();
  }

  // Active Owner STEP Payor Role Reconciliation
  async function makeOwnerPayorForInsured(insuredType: InsuredType) {
    const insured = useInsuredStore(insuredType, pinia);
    const eApp = useEappStore(pinia);

    if (insured.payorKey && insured.payorKey === eApp.ownerKey) return;
    if (insured.payorKey) await insured.deletePayor();
    const owner = getPartyStore(eApp.ownerKey, pinia);
    await owner.addPayorRole({ insured_id: insured.id });
  }
  async function reconcileActiveOwnerStepPayors() {
    const eApp = useEappStore(pinia);
    // Active Owner STEP = owner is payor = Delete non-owner payors, add owner payor role
    await makeOwnerPayorForInsured(INSURED_TYPES.INSURED);
    if (eApp.isMultiplePolicies)
      await makeOwnerPayorForInsured(INSURED_TYPES.JOINT);
  }
  async function defaultPayorRoleToOwner() {
    const insured = usePrimaryInsuredStore(pinia);
    const eApp = useEappStore(pinia);

    if (insured.payorKey) return;
    const owner = getPartyStore(eApp.ownerKey, pinia);
    await owner.addPayorRole({ insured_id: insured.id });
  }

  return {
    $reset,
    ...state,
    step,
    canSelectPayor,
    previous,
    next,
    setStepRequirements,
    beforeLoadAction,
    reconcilePayorRoles,
    reconcileDualInsuredSinglePolicy,
    makePrimaryInsuredPayor,
    deleteJointNonInsuredPayors,
    reconcileInactiveOwnerStepPayors,
    makeOwnerPayorForInsured,
    reconcileActiveOwnerStepPayors,
    defaultPayorRoleToOwner,
  };
}

export const useStepPaymentStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, PaymentStep>("step-payment", () => {
    return initializePaymentStore(pinia);
  })(pinia, hot);
};
